"use client";

import Link from "next/link";
import { Avatar } from "./ui/Avatar";
import type { ThetaEvent } from "@/lib/hooks/useEvents";
import { attendeeCountLabel, interestedCountLabel } from "@/lib/counts";

/**
 * How many of the server's `goingAvatars` the card shows.
 *
 * The wire sends twelve (`GOING_AVATAR_LIMIT`), and twelve `w-8` bubbles at a
 * 6px overlap are ~318px of unshrinkable flex item — wider than the whole
 * content box of a card on a phone, so the row spilled out of the card and
 * took the attendee count with it (OSO-392). Five fits the narrowest layout,
 * and nothing is lost by stopping there: the exact number is written out in
 * words right beside them.
 */
const MAX_GOING_AVATARS = 5;

const MONTH_SHORT = [
  "JAN", "FEB", "MAR", "APR", "MAJ", "JUN",
  "JUL", "AUG", "SEP", "OKT", "NOV", "DEC",
];

function formatDateRange(startsAt: string, endsAt: string | null): string {
  const start = new Date(startsAt);
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  };
  let text = start.toLocaleDateString("da-DK", opts);
  if (endsAt) {
    const end = new Date(endsAt);
    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();
    if (sameDay) {
      text += ` - ${end.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })}`;
    } else {
      text += ` - ${end.toLocaleDateString("da-DK", opts)}`;
    }
  }
  return text;
}

export function EventCard({ event }: { event: ThetaEvent }) {
  const start = new Date(event.startsAt);
  const month = MONTH_SHORT[start.getMonth()];
  const day = start.getDate();
  const isPast = start < new Date();

  return (
    <Link
      href={`/begivenheder/${event.id}`}
      className={`block bg-surface rounded-xl border border-border overflow-hidden hover:shadow-md transition-shadow ${isPast ? "opacity-70" : ""}`}
    >
      <div className="relative h-40 sm:h-48 overflow-hidden">
        {event.coverUrl ? (
          <img
            src={event.coverUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400" />
        )}
        <div className="absolute top-3 left-3 bg-surface rounded-lg shadow-lg px-2.5 py-1.5 text-center min-w-[48px]">
          <div className="text-[10px] font-bold text-red-500 uppercase leading-tight">
            {month}
          </div>
          <div className="text-lg font-bold text-foreground leading-tight">
            {day}
          </div>
        </div>
        {/* Public badge. `isPubliclyVisible`, not `!isPrivate` — see the same
            badge on the event page for why the flag is only half the rule. */}
        {event.isPubliclyVisible && (
          <div className="absolute top-3 right-3 bg-green-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shadow">
            Offentlig
          </div>
        )}
      </div>

      <div className="p-4">
        <p className="text-xs font-medium text-primary uppercase">
          {formatDateRange(event.startsAt, event.endsAt)}
        </p>

        <h3 className="font-bold text-foreground mt-1 text-lg leading-tight">
          {event.title}
        </h3>

        {event.location && (
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {event.location}
          </p>
        )}

        <div className="flex items-center gap-3 mt-3">
          {event.goingAvatars.length > 0 && (
            <div className="flex -space-x-1.5 shrink-0">
              {/* Keyed by ID. `username` is gone from the wire (O-21) and was
                  the key here. */}
              {event.goingAvatars.slice(0, MAX_GOING_AVATARS).map((a) => (
                <div key={a.id} className="border-2 border-surface rounded-full">
                  <Avatar url={a.avatar.medium} name={a.name ?? ""} size="sm" />
                </div>
              ))}
            </div>
          )}
          <p className="min-w-0 text-xs text-muted-foreground">
            {event.rsvpSummary.goingCount > 0 && attendeeCountLabel(event.rsvpSummary.goingCount)}
            {event.rsvpSummary.goingCount > 0 && event.rsvpSummary.interestedCount > 0 && " \u00b7 "}
            {event.rsvpSummary.interestedCount > 0 && interestedCountLabel(event.rsvpSummary.interestedCount)}
            {event.rsvpSummary.goingCount === 0 && event.rsvpSummary.interestedCount === 0 && "Ingen tilmeldte endnu"}
            {event.commentCount > 0 && (
              <>
                {" \u00b7 "}
                {event.commentCount} kommentar{event.commentCount !== 1 ? "er" : ""}
              </>
            )}
          </p>
        </div>

        {event.groups.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {event.groups.map((g) => (
              <span
                key={g.id}
                className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full"
              >
                {g.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
